import { useState } from "react";
import { Plus, MapPin, Sparkles } from "lucide-react";
import LegsModal from "../Modals/LegsModal.jsx";
import CategoriesModal from "../Modals/CategoriesModal.jsx";
import { DEFAULT_CATEGORIES, LEG_COLOURS, resolveIcon } from "../../constants.js";
import { toISO, addDays } from "../../utils/date.js";

const SUGGESTED_TITLE = "My trip";

function makeStarterLeg() {
  const today = new Date();
  const start = toISO(today);
  const end = toISO(addDays(today, 6));
  return [{
    id: `leg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    city: "",
    country: "",
    start,
    end,
    emoji: "📍",
    colour: LEG_COLOURS[0],
    tentative: false,
  }];
}

export default function LandingFlow({ onCreate, busy, error }) {
  const [title, setTitle] = useState(SUGGESTED_TITLE);
  const [categories, setCategories] = useState(() => JSON.parse(JSON.stringify(DEFAULT_CATEGORIES)));
  const [legs, setLegs] = useState(makeStarterLeg);
  const [legsOpen, setLegsOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [localError, setLocalError] = useState(null);

  const submit = () => {
    setLocalError(null);
    const trimmed = title.trim();
    if (!trimmed) return setLocalError("Title is required");
    if (!categories.length) return setLocalError("Add at least one category");
    if (!legs.length) return setLocalError("Add at least one leg");
    const incomplete = legs.find((l) => !l.city?.trim() || !l.country?.trim());
    if (incomplete) return setLocalError("Every leg needs a city and country");
    onCreate({ title: trimmed, categories, legs });
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center px-4 pt-10 pb-20 max-w-xl mx-auto w-full">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400 mb-2">The Itinerary</div>
        <h1
          className="text-3xl font-semibold text-slate-900 leading-tight text-center"
          style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: "-0.02em" }}
        >
          Plan a new trip
        </h1>
        <p className="mt-3 text-sm text-slate-600 text-center max-w-md leading-relaxed">
          Set a title, the legs of the trip, and the categories you'll use. Once saved, you'll get a URL you can share with friends — and pieces of it you can share separately.
        </p>

        <div className="mt-8 w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-5">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Trip title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Bangkok → London → Croatia"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-slate-900 focus:outline-none text-slate-900"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
            />
            <p className="text-[11px] text-slate-500 mt-1.5">Title is fixed once the trip is created.</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Legs</label>
              <button
                type="button"
                onClick={() => setLegsOpen(true)}
                className="text-xs text-slate-700 hover:text-slate-900 font-medium underline-offset-4 hover:underline"
              >
                Edit
              </button>
            </div>
            <div className="space-y-1.5">
              {legs.map((leg, i) => (
                <div
                  key={leg.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl border bg-white"
                  style={{ borderColor: `${leg.colour}40` }}
                >
                  <span className="w-2 h-6 rounded-full" style={{ background: leg.colour }} />
                  <span className="text-lg">{leg.emoji || "📍"}</span>
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="font-medium text-slate-900 truncate">
                      {leg.city ? `${leg.city}, ${leg.country}` : `Leg ${i + 1}`}
                    </div>
                    <div className="text-[11px] text-slate-500">{leg.start} → {leg.end}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Categories</label>
              <button
                type="button"
                onClick={() => setCatsOpen(true)}
                className="text-xs text-slate-700 hover:text-slate-900 font-medium underline-offset-4 hover:underline"
              >
                Edit
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => {
                const Icon = resolveIcon(c.icon);
                return (
                  <span
                    key={c.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium"
                    style={{ background: `${c.colour}10`, color: c.colour, borderColor: `${c.colour}40` }}
                  >
                    <Icon size={11} />
                    {c.label}
                  </span>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">Categories are fixed once the trip is created.</p>
          </div>

          {displayError && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700">
              {displayError}
            </div>
          )}

          <button
            onClick={submit}
            disabled={busy}
            className="w-full px-5 py-3 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? "Creating trip…" : "Create cloud trip"}
          </button>
          <p className="text-[11px] text-slate-500 text-center -mt-2">
            Your trip will live in the cloud at a URL only you and people you share with can see.
          </p>
        </div>

        <div className="mt-8 text-xs text-slate-500 max-w-md text-center leading-relaxed flex items-start gap-2">
          <MapPin size={14} className="mt-0.5 flex-shrink-0" />
          <span>The URL is the password. Anyone with it can edit the legs in the URL — there are no separate accounts.</span>
        </div>
      </div>

      <LegsModal
        open={legsOpen}
        legs={legs}
        onClose={() => setLegsOpen(false)}
        onSave={(next) => setLegs(next)}
        tripId={null}
      />
      <CategoriesModal
        open={catsOpen}
        categories={categories}
        items={{}}
        onClose={() => setCatsOpen(false)}
        onSave={(next) => setCategories(next)}
      />
    </div>
  );
}
